import { useEffect, useRef, useState, type ReactNode, type ButtonHTMLAttributes } from "react";
import { Check, X } from "lucide-react";
import { initials } from "@/lib/format";

export function Button({ variant, size, className = "", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "light" | "danger" | "ai"; size?: "sm" | "md" }) {
  return <button type="button" className={`btn ${variant ?? ""} ${size ?? ""} ${className}`} {...rest} />;
}

export function Pill({ color, children, className = "", onClick }: { color?: "red" | "yellow" | "green" | "accent" | "bright"; children: ReactNode; className?: string; onClick?: () => void }) {
  return <span className={`pill ${color ?? ""} ${className}`} onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>{children}</span>;
}

export function Tag({ color = "neutral", children }: { color?: "ai" | "accent" | "red" | "yellow" | "green" | "neutral" | "outline"; children: ReactNode }) {
  return <span className={`tag ${color}`}>{children}</span>;
}

export const AiTag = () => <span className="tag ai" title="Done by Claude">AI</span>;

export function Dot({ color, pulse, size }: { color?: "red" | "yellow" | "green" | "blue" | null; pulse?: boolean; size?: "lg" }) {
  return <span className={`dot ${color ?? ""} ${pulse ? "pulse" : ""} ${size ?? ""}`} />;
}

export function Avatar({ name, large }: { name: string; large?: boolean }) {
  return <div className={`avatar ${large ? "lg" : ""}`}>{initials(name)}</div>;
}

export function Card({ title, actions, children, className = "", color }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; color?: "red" | "yellow" }) {
  return (
    <section className={`card ${color ?? ""} ${className}`}>
      {title !== undefined && (
        <div className="card-head">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          {actions && <div className="ml-auto row gap-8">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Checkbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} aria-label={label} className={`checkbox ${checked ? "checked" : ""}`} onClick={() => onChange(!checked)}>
      {checked && <Check size={13} strokeWidth={2.5} />}
    </button>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`toggle ${on ? "on" : ""}`} onClick={() => onChange(!on)} />;
}

export function Spinner() {
  return <span className="spinner" aria-label="Loading" />;
}

export function Loading({ text = "Loading" }: { text?: string }) {
  return (
    <div className="loading">
      <Spinner /> {text}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? "wide" : ""}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <Button variant="ghost" size="sm" className="icon ml-auto" onClick={onClose} aria-label="Close"><X size={14} /></Button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint , className = "" }: { label: string; children: ReactNode; hint?: string ; className?: string }) {
  return (
    <div className={`field ${className}`}>
      <label>{label}</label>
      {children}
      {hint && <span className="xs dim">{hint}</span>}
    </div>
  );
}

export function Menu({ trigger, items }: { trigger: ReactNode; items: { label: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <span onClick={() => setOpen((o) => !o)}>{trigger}</span>
      {open && (
        <div className="menu" style={{ right: 0, top: "calc(100% + 4px)" }}>
          {items.map((it) => (
            <button key={it.label} onClick={() => { setOpen(false); it.onClick(); }} style={it.danger ? { color: "var(--status-red-text)" } : undefined}>{it.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Toasts ----------
type Toast = { id: number; text: string; error?: boolean };
let pushToast: ((t: Omit<Toast, "id">) => void) | null = null;
export function toast(text: string, error = false) { pushToast?.({ text, error }); }
export function ToastHost() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => {
    let n = 0;
    pushToast = (t) => {
      const id = ++n;
      setItems((s) => [...s, { ...t, id }]);
      setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), t.error ? 6000 : 3000);
    };
    return () => { pushToast = null; };
  }, []);
  return (
    <div className="toast-wrap">
      {items.map((t) => <div key={t.id} className={`toast ${t.error ? "error" : ""}`}>{t.text}</div>)}
    </div>
  );
}

export function PageHeader({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {sub && <span className="date">{sub}</span>}
      {children && <div className="ml-auto row gap-8 wrap">{children}</div>}
    </div>
  );
}
